import axios from 'axios';
import config from '../config';
import { mockLectureData } from './mockData';
import { getPrimarySpeaker } from '../utils/getPrimarySpeaker';
import { getSchemaManager } from '../lib/schema-config';

const SHEETS_API_KEY = config.GOOGLE_API_KEY;

// Schema manager instance (Milestone 4 - Schema-Aware Dynamic Mapping)
let schemaManagerInstance = null;
let columnMappingCache = null;
let dynamicSheetRange = null; // Will be set from schema

// Helper function to get the appropriate API URL (with or without CORS proxy)
const getApiUrl = (url) => {
  // Safety check - prefix with CORS proxy if needed
  if (config.USE_CORS_PROXY) {
    // Check if the URL already has a protocol
    const hasProtocol = url.startsWith('http://') || url.startsWith('https://');
    if (!hasProtocol) {
      url = `https://${url}`;
    }
    return `${config.CORS_PROXY_URL}${encodeURIComponent(url)}`;
  }
  return url;
};

/**
 * Convert time string (MM:SS or M:SS) to seconds
 * Handles special "end" value which returns null (meaning play to end of video)
 * @param {string} timeStr - Time string in MM:SS format or "end"
 * @returns {number|null} Time in seconds, or null if "end" or invalid
 */
const convertTimeToSeconds = (timeStr) => {
  if (!timeStr || typeof timeStr !== 'string') {
    return null;
  }

  const trimmed = timeStr.trim().toLowerCase();

  // Handle special "end" value
  if (trimmed === 'end' || trimmed === '') {
    return null;
  }

  // Parse HH:MM:SS or H:MM:SS format (e.g., "0:02:17", "1:32:45")
  const hhmmssMatch = trimmed.match(/^(\d{1,2}):(\d{2}):(\d{2})$/);
  if (hhmmssMatch) {
    const hours = parseInt(hhmmssMatch[1], 10);
    const minutes = parseInt(hhmmssMatch[2], 10);
    const seconds = parseInt(hhmmssMatch[3], 10);
    return (hours * 3600) + (minutes * 60) + seconds;
  }

  // Parse MM:SS or M:SS format (e.g., "2:17", "32:27")
  const mmssMatch = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (mmssMatch) {
    const minutes = parseInt(mmssMatch[1], 10);
    const seconds = parseInt(mmssMatch[2], 10);
    return (minutes * 60) + seconds;
  }

  // Try parsing as just seconds (if someone enters "90" meaning 90 seconds)
  const justSeconds = parseInt(trimmed, 10);
  if (!isNaN(justSeconds)) {
    return justSeconds;
  }

  console.warn(`[convertTimeToSeconds] Could not parse time string: "${timeStr}"`);
  return null;
};

/**
 * Convert column letter (A, B, C, etc.) to zero-based index
 * @param {string} letter - Column letter (A, B, C, etc.)
 * @returns {number} Zero-based column index
 */
const columnLetterToIndex = (letter) => {
  if (!letter || typeof letter !== 'string') {
    throw new Error(`Invalid column letter: ${letter}`);
  }

  let index = 0;
  const upperLetter = letter.toUpperCase();

  for (let i = 0; i < upperLetter.length; i++) {
    index = index * 26 + (upperLetter.charCodeAt(i) - 'A'.charCodeAt(0) + 1);
  }

  return index - 1; // Convert to zero-based
};

/**
 * Fetch and parse lecture data from the Google Sheet
 * @param {string} sheetId - The ID of the Google Sheet to fetch data from.
 * @returns {Promise<Array>} Array of lecture objects
 */
export const fetchLectureData = async (sheetId) => {
  if (!sheetId) {
    console.error('fetchLectureData called without a sheetId!');
    return []; // Return empty array or throw error if no sheetId provided
  }
  try {
    // Fetch schema configuration (Milestone 4 - Schema-Aware Dynamic Mapping)
    if (!schemaManagerInstance) {
      schemaManagerInstance = getSchemaManager();
    }

    if (!columnMappingCache) {
      try {
        const schema = await schemaManagerInstance.fetchActiveSchema();
        if (!schema || !schema.columns) {
          throw new Error('No active schema found in database');
        }

        // Build column mapping: { fieldName: columnIndex }
        columnMappingCache = {};
        schema.columns.forEach(col => {
          const fieldName = col.field_name;
          const columnLetter = col.column_letter;
          columnMappingCache[fieldName] = columnLetterToIndex(columnLetter);
        });

        console.log('✅ Schema loaded successfully. Column mapping:', columnMappingCache);

        // Get dynamic sheet range from schema
        dynamicSheetRange = await schemaManagerInstance.getSheetRange();
        console.log('✅ Dynamic sheet range from schema:', dynamicSheetRange);
      } catch (schemaError) {
        console.error('❌ CRITICAL: Failed to load schema configuration:', schemaError);
        throw new Error(`Schema configuration required but unavailable: ${schemaError.message}`);
      }
    }

    const cleanId = extractSheetId(sheetId);
    const isAknySheet = cleanId === '17a6XGmRZfzqiGMWvlJvvqHz-NEKUiDCJIEBQWCnUaFo'; // Flag for AKNY sheet

    if (isAknySheet) console.log('--- AKNY Sheet Debug Start ---');
    console.log('Fetching lecture data from spreadsheet ID (raw):', sheetId);
    console.log('Using cleaned sheet ID:', cleanId);

    // Use dynamic range from schema, fallback to config if not available
    const effectiveRange = dynamicSheetRange || config.SHEET_RANGE;
    console.log('Using sheet range:', effectiveRange);

    // Fetch data from Google Sheets API
    const response = await fetch(
      `${config.GOOGLE_SHEETS_API}/${cleanId}/values/${effectiveRange}?key=${SHEETS_API_KEY}`
    );
    
    if (!response.ok) {
      throw new Error(`Google Sheets API request failed: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    
    if (!data.values || data.values.length <= 1) {
      console.warn('No data found in spreadsheet or only header row exists');
      return [];
    }
    
    // Log the raw data for debugging
    console.log('Raw spreadsheet data:', data.values);
    console.log(`Found ${data.values.length - 1} rows of lecture data`);
    
    // Get column headers for reference
    const headers = data.values[0];
    console.log('Column headers:', headers);
    
    // Find indexes of important columns with flexible name matching
    const findColumnIndex = (possibleNames) => {
      for (const name of possibleNames) {
        const index = headers.findIndex(header => 
          header && typeof header === 'string' && header.toLowerCase().includes(name.toLowerCase())
        );
        if (index >= 0) {
          if (isAknySheet) console.log(`[AKNY Debug] Found column '${name}' at index ${index} with header: '${headers[index]}'`); // AKNY Debug
          else console.log(`Found column '${name}' at index ${index} with header: '${headers[index]}'`);
          return index;
        }
      }
      if (isAknySheet) console.log(`[AKNY Debug] Column not found for names: ${possibleNames.join(', ')}`); // AKNY Debug
      return -1;
    };
    
    // Find columns by trying various possible names
    const summaryVideoIndex = findColumnIndex(['summary video', 'summaryVideo', 'summary_video', 'summaryVideoURL']);
    const fullVideoIndex = findColumnIndex(['full video', 'fullVideo', 'full_video', 'fullVideoURL']);
    const keyTakeawaysIndex = findColumnIndex(['key takeaways', 'keyTakeaways', 'key_takeaways', '5 key takeaway']);
    const detailedNotesIndex = findColumnIndex(['detailed notes', 'detailedNotes', 'detailed_notes', 'DetailedNotes']);
    const summaryDocIdIndex = findColumnIndex(['summary doc id', 'summaryDocId', 'summary_doc_id', 'Summary Doc ID']);
    
    console.log('Column indexes found:', {
      summaryVideo: summaryVideoIndex,
      fullVideo: fullVideoIndex,
      keyTakeaways: keyTakeawaysIndex,
      detailedNotes: detailedNotesIndex
    });
    if (isAknySheet) console.log('[AKNY Debug] Column indexes found:', {
      summaryVideo: summaryVideoIndex,
      fullVideo: fullVideoIndex,
      keyTakeaways: keyTakeawaysIndex,
      detailedNotes: detailedNotesIndex
    }); // AKNY Debug
    
    // Validate that all required fields are present in schema (Milestone 4)
    // Note: is_recurring_event is optional - only present for newer entries
    const requiredFields = [
      'lectureId', 'event', 'lectureSeriesId', 'lectureIdInSeries',
      'title', 'speaker', 'date', 'summaryVideoUrl', 'fullVideoUrl',
      'keyTakeawaysUrl', 'detailedNotesUrl', 'driveFolderUrl'
    ];

    const missingFields = requiredFields.filter(field => columnMappingCache[field] === undefined);

    if (missingFields.length > 0) {
      throw new Error(`❌ Schema missing required fields: ${missingFields.join(', ')}. Please update schema configuration in Supabase.`);
    }

    // Use schema-based column indexes for URL fields (NO hardcoded fallbacks)
    const columnIndexes = {
      summaryVideo: columnMappingCache['summaryVideoUrl'],
      fullVideo: columnMappingCache['fullVideoUrl'],
      keyTakeaways: columnMappingCache['keyTakeawaysUrl'],
      detailedNotes: columnMappingCache['detailedNotesUrl'],
      summaryDocId: summaryDocIdIndex,
    };

    console.log('✅ Schema-based column mapping validated. All required fields present.');
    if (isAknySheet) console.log('[AKNY Debug] Schema-based column indexes:', columnIndexes); // AKNY Debug
    
    // Map data rows to lecture objects
    // Note: First row (index 0) contains headers, so we skip it with slice(1)
    const lectures = data.values.slice(1).map((row, index) => {
      // Skip empty rows
      if (!row.length || !row[0]) {
        return null;
      }

      // Skip lectures that aren't fully processed — only show completed jobs
      const statusColIndex = columnMappingCache['status'];
      if (statusColIndex !== undefined) {
        const status = (row[statusColIndex] || '').trim().toLowerCase();
        if (status && status !== 'completed') {
          return null;
        }
      }

      // Clean and validate URLs - trim whitespace and ensure they have a protocol
      const cleanUrl = (url) => {
        if (!url) return '';
        let cleaned = url.trim();
        // If it looks like a URL but doesn't have a protocol, add https://
        if (cleaned && !cleaned.startsWith('http://') && !cleaned.startsWith('https://')) {
          if (cleaned.includes('docs.google.com') || cleaned.includes('drive.google.com') || 
              cleaned.includes('youtube.com') || cleaned.includes('youtu.be')) {
            cleaned = 'https://' + cleaned;
          }
        }
        return cleaned;
      };
      
      // Extract URLs using detected column indexes
      const summaryVideoUrl = row[columnIndexes.summaryVideo] ? cleanUrl(row[columnIndexes.summaryVideo]) : '';
      const fullVideoUrl = row[columnIndexes.fullVideo] ? cleanUrl(row[columnIndexes.fullVideo]) : '';
      const keyTakeawaysUrl = row[columnIndexes.keyTakeaways] ? cleanUrl(row[columnIndexes.keyTakeaways]) : '';
      const detailedNotesUrl = row[columnIndexes.detailedNotes] ? cleanUrl(row[columnIndexes.detailedNotes]) : '';
      const summaryDocId = row[columnIndexes.summaryDocId] ? row[columnIndexes.summaryDocId].trim() : '';
      
      // Log all extracted URLs for this lecture for debugging
      console.log(`Row ${index + 1} (Lecture ID: ${row[0]}) URLs:`, {
        summaryVideo: summaryVideoUrl,
        fullVideo: fullVideoUrl,
        keyTakeaways: keyTakeawaysUrl,
        detailedNotes: detailedNotesUrl
      });
      // AKNY Specific Debug Logging for the first 3 data rows (index 0, 1, 2)
      if (isAknySheet && index < 3) { 
        console.log(`[AKNY Debug] Row ${index + 1} Raw Data:`, row);
        console.log(`[AKNY Debug] Row ${index + 1} Extracted URLs:`, {
            summaryVideoUrl, 
            fullVideoUrl, 
            keyTakeawaysUrl, 
            detailedNotesUrl, 
            summaryIndex: columnIndexes.summaryVideo, 
            fullIndex: columnIndexes.fullVideo
        });
      }
      
      // Create lecture object using schema-based mapping (Milestone 4)
      const lectureIdValue = row[columnMappingCache['lectureId']];
      const eventValue = row[columnMappingCache['event']];

      // Extract clip time bounds (for video player start/end constraints)
      const rawStartTime = columnMappingCache['startTime'] !== undefined
        ? row[columnMappingCache['startTime']]
        : null;
      const rawEndTime = columnMappingCache['endTime'] !== undefined
        ? row[columnMappingCache['endTime']]
        : null;

      // Convert MM:SS format to seconds (null if "end" or empty)
      const clipStartTime = convertTimeToSeconds(rawStartTime);
      const clipEndTime = convertTimeToSeconds(rawEndTime);

      // Parse isRecurringEvent flag (optional field - may not exist in older schema)
      const rawIsRecurringEvent = columnMappingCache['is_recurring_event'] !== undefined
        ? row[columnMappingCache['is_recurring_event']]
        : null;
      const isRecurringEvent = rawIsRecurringEvent === 'TRUE' || rawIsRecurringEvent === true || rawIsRecurringEvent === 'true';

      // Extract optional Infographics and Viral Clips URLs (may not exist in schema)
      const rawInfographicsUrls = columnMappingCache['infographicsUrls'] !== undefined
        ? row[columnMappingCache['infographicsUrls']] || ''
        : '';
      const rawViralClipsUrls = columnMappingCache['viralClipsUrls'] !== undefined
        ? row[columnMappingCache['viralClipsUrls']] || ''
        : '';
      // V8.4: AI-dubbed video URL (optional — schema v2.2.3+, column BD)
      const rawDubbedVideoUrl = columnMappingCache['dubbedVideoUrl'] !== undefined
        ? row[columnMappingCache['dubbedVideoUrl']] || ''
        : '';

      return {
        lectureId: lectureIdValue?.toString() || (index + 1).toString(),
        event: (eventValue || 'Uncategorized').trim(),
        series: (eventValue || 'Uncategorized').trim(),  // Use the same value for both event and series
        lectureSeriesId: row[columnMappingCache['lectureSeriesId']] || '',
        lectureIdInSeries: row[columnMappingCache['lectureIdInSeries']] || '',
        title: row[columnMappingCache['title']] || `Lecture ${lectureIdValue || index + 1}`,
        speaker: row[columnMappingCache['speaker']] || '',
        date: row[columnMappingCache['date']] || '',
        summaryVideoUrl,
        fullVideoUrl,
        keyTakeawaysUrl,
        detailedNotesUrl,
        summaryDocId,
        driveFolderUrl: row[columnMappingCache['driveFolderUrl']] ? cleanUrl(row[columnMappingCache['driveFolderUrl']]) : '',
        // Clip time bounds (in seconds) for video player
        clipStartTime,  // Start time in seconds, or null if not specified
        clipEndTime,    // End time in seconds, or null if "end" or not specified
        // Recurring event flag (skip auto-assignment of lecture numbers)
        isRecurringEvent,  // Boolean: true for recurring events like Jummah sermons
        // Infographics and Viral Clips (optional - newline-separated URLs in single cell)
        infographicsUrls: rawInfographicsUrls,  // Raw string from Google Sheets cell
        viralClipsUrls: rawViralClipsUrls,       // Raw string from Google Sheets cell
        // V8.4: AI-dubbed video (English, cloned voice) Drive URL
        dubbedVideoUrl: rawDubbedVideoUrl ? cleanUrl(rawDubbedVideoUrl) : ''
      };
    });
    
    // Filter out null entries (from empty rows)
    const filteredLectures = lectures.filter(lecture => lecture !== null);
    
    console.log(`Processed ${filteredLectures.length} lectures after filtering empty rows`);
    
    // Debug log the first few lecture objects to verify data mapping
    filteredLectures.slice(0, 2).forEach((lecture, idx) => {
      console.log(`Lecture ${idx + 1} object:`, {
        lectureId: lecture.lectureId,
        title: lecture.title,
        summaryVideoUrl: lecture.summaryVideoUrl,
        fullVideoUrl: lecture.fullVideoUrl,
        keyTakeawaysUrl: lecture.keyTakeawaysUrl,
        detailedNotesUrl: lecture.detailedNotesUrl  // This is what we're most interested in
      });
    });
    
    if (isAknySheet) console.log('--- AKNY Sheet Debug End ---');

    return filteredLectures;
  } catch (error) {
    console.error('Error fetching lecture data:', error);
    // Optionally log the specific sheet ID causing the error
    console.error(`Error occurred for sheetId: ${sheetId}`);
    throw error; // Re-throw error to be handled by calling components
  }
};

// Helper to extract the raw Google Sheet ID from a variety of inputs
// Accepts either:
//   1. The raw ID (e.g. 1QG4gts3BHd8dypmxuMiybIhsl8wq_jqjMDfqgZoeaqc)
//   2. A full URL (e.g. https://docs.google.com/spreadsheets/d/<ID>/edit#gid=0)
// Returns the cleaned ID so it can be safely inserted into the Google Sheets API path.
const extractSheetId = (input) => {
  if (!input) return '';
  console.log('[extractSheetId] Input:', input);
  // If it's already just an ID (no http present), return as-is
  if (!input.includes('http')) {
    console.log('[extractSheetId] Input appears to be a raw ID.');
    return input;
  }

  // Try standard /d/<ID>/ pattern first
  const match = input.match(/\/d\/([a-zA-Z0-9-_]+)/);
  if (match && match[1]) {
    console.log('[extractSheetId] Extracted ID via regex:', match[1]);
    return match[1];
  }

  console.log('[extractSheetId] Regex match failed. Trying URL parser fallback...');

  // Fallback: use URL parser and take segment after "d"
  try {
    const urlObj = new URL(input);
    const parts = urlObj.pathname.split('/');
    const dIndex = parts.indexOf('d');
    if (dIndex !== -1 && parts.length > dIndex + 1) {
      return parts[dIndex + 1];
    }
  } catch (e) {
    // Ignore URL parsing errors and fall through
    console.error('[extractSheetId] URL parsing failed:', e);
  }

  // If we reach here, neither regex nor URL parsing worked
  console.warn(`[extractSheetId] Could not extract ID from: ${input}. Returning original input.`);
  return input;
};

/**
 * Process mock data to include lectureIdInSeries
 * @returns {Array} Processed mock lecture data
 */
const processMockData = () => {
  console.log('Using mock data');
  return mockLectureData;
};

/**
 * Assign lectureIdInSeries to lectures grouped by event/series
 * Respects the isRecurringEvent flag - recurring events are NOT auto-assigned
 * @param {Array} lectures - Array of lecture objects
 * @returns {Array} Lectures with lectureIdInSeries assigned
 */
const assignLectureIdInSeries = (lectures) => {
  // Group lectures by event/series
  const lecturesByEvent = {};

  // Group lectures by event
  lectures.forEach(lecture => {
    const event = lecture.event || 'Uncategorized';

    if (!lecturesByEvent[event]) {
      lecturesByEvent[event] = [];
    }

    lecturesByEvent[event].push(lecture);
  });

  // For each event, assign sequential IDs if not already present
  Object.keys(lecturesByEvent).forEach(event => {
    const eventLectures = lecturesByEvent[event];

    // Sort lectures by title or date if available
    eventLectures.sort((a, b) => {
      // If both have numerical IDs, sort by those
      const aId = parseInt(a.lectureId.replace(/\D/g, '')) || 0;
      const bId = parseInt(b.lectureId.replace(/\D/g, '')) || 0;

      return aId - bId;
    });

    // Assign lectureIdInSeries if not already present
    // IMPORTANT: Skip auto-assignment for recurring events (isRecurringEvent === true)
    eventLectures.forEach((lecture, index) => {
      // Skip auto-assignment if:
      // 1. lectureIdInSeries is already present, OR
      // 2. This is a recurring event (explicitly marked)
      if (!lecture.lectureIdInSeries && !lecture.isRecurringEvent) {
        lecture.lectureIdInSeries = (index + 1).toString();
      }
    });
  });

  return lectures;
};

/**
 * Convert spreadsheet header to camelCase key
 * @param {string} header - The header from the spreadsheet
 * @returns {string} camelCase key for the object
 */
const convertHeaderToKey = (header) => {
  // Handle special cases
  switch (header.trim().toLowerCase()) {
    case 'lecture id':
      return 'lectureId';
    case 'l-id in series':
      return 'lectureIdInSeries';
    case 'key takeaways url':
      return 'keyTakeawaysUrl';
    case 'detailed notes url':
      return 'detailedNotesUrl';
    case 'summary video url':
      return 'summaryVideoUrl';
    case 'full video url':
      return 'fullVideoUrl';
    case 'drive folder url':
      return 'driveFolderUrl';
    default:
      // Convert to camelCase
      return header
        .trim()
        .toLowerCase()
        .replace(/\s+(.)/g, (match, group) => group.toUpperCase());
  }
};

/**
 * Extract Google Doc ID from URL
 * @param {string} url - Google Docs URL
 * @returns {string|null} Google Doc ID or null if not found
 */
const extractGoogleDocId = (url) => {
  if (!url) return null;
  
  try {
    console.log('Extracting Doc ID from URL:', url);
    
    // Handle the case when the URL is just the ID itself
    if (/^[a-zA-Z0-9_-]{25,}$/.test(url)) {
      console.log('URL appears to be a direct ID:', url);
      return url;
    }
    
    // Handle different Google Docs URL formats
    
    // Format: https://docs.google.com/document/d/[ID]/edit
    const standardMatch = url.match(/docs\.google\.com\/document\/d\/([a-zA-Z0-9_-]+)(\/|\?|$)/);
    if (standardMatch && standardMatch[1]) {
      console.log('Matched standard Google Docs URL, ID:', standardMatch[1]);
      return standardMatch[1];
    }
    
    // Format: https://drive.google.com/open?id=[ID]
    const driveOpenMatch = url.match(/drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/);
    if (driveOpenMatch && driveOpenMatch[1]) {
      console.log('Matched Drive open URL, ID:', driveOpenMatch[1]);
      return driveOpenMatch[1];
    }
    
    // Format: https://drive.google.com/file/d/[ID]/view
    const driveFileMatch = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
    if (driveFileMatch && driveFileMatch[1]) {
      console.log('Matched Drive file URL, ID:', driveFileMatch[1]);
      return driveFileMatch[1];
    }
    
    // Last resort: look for any long alphanumeric string that could be an ID
    const anyIdMatch = url.match(/([a-zA-Z0-9_-]{25,})/);
    if (anyIdMatch && anyIdMatch[1]) {
      console.log('Found potential ID using generic pattern:', anyIdMatch[1]);
      return anyIdMatch[1];
    }
    
    console.warn('No Google Doc ID found in URL:', url);
    return null;
  } catch (error) {
    console.error('Error extracting Google Doc ID:', error);
    return null;
  }
};

/**
 * Fetch document content from Google Docs or Word document
 * @param {string} documentUrl - URL to the Google Doc or Word document
 * @returns {Promise<string>} Document content as HTML
 */
export const fetchDocumentContent = async (documentUrl) => {
  if (!documentUrl) {
    console.warn('No document URL provided');
    return 'No content available. Please check back later.';
  }
  
  try {
    // Extract Google Doc ID from URL
    console.log('Attempting to fetch document content from:', documentUrl);
    const docId = extractGoogleDocId(documentUrl);
    
    if (!docId) {
      console.warn('Invalid or unrecognized document URL format:', documentUrl);
      return 'Unable to load content. Invalid document URL format.';
    }
    
    console.log('Successfully extracted document ID:', docId);
    
    // Multiple approaches to try fetching the content
    const approaches = [
      // Approach 1: Try export as HTML format (Prioritized for better formatting)
      async () => {
        const exportUrl = `https://docs.google.com/document/d/${docId}/export?format=html`;
        console.log('Trying HTML export URL:', exportUrl);

        const response = await fetch(exportUrl, {
          method: 'GET',
          mode: 'cors' // Ensure CORS is handled correctly
        });

        if (!response.ok) {
          console.log(`HTML export failed with status: ${response.status}`);
          return null;
        }

        const html = await response.text();
        if (!html || html.length < 50) { // Basic check for meaningful content
          console.log('HTML export returned empty or very short content');
          return null;
        }

        console.log('Successfully fetched as HTML, length:', html.length);
        return cleanGoogleDocsExportedHtml(html); // Clean the HTML
      },
      
      // Approach 2: Try published document (If available and HTML export fails)
      async () => {
        // Attempt to infer the published URL structure
        const pubUrl = `https://docs.google.com/document/d/e/${docId}/pub`; 
        console.log('Trying published doc URL:', pubUrl);

        // Use a CORS proxy as direct fetch might be blocked
        const corsProxyUrl = `https://corsproxy.io/?${encodeURIComponent(pubUrl)}`;

        const response = await fetch(corsProxyUrl);

        if (!response.ok) {
          console.log(`Published doc fetch failed with status: ${response.status}`);
          return null;
        }

        const html = await response.text();
        if (!html || html.length < 50) {
          console.log('Published doc returned empty or very short content');
          return null;
        }

        console.log('Successfully fetched published doc, length:', html.length);
        // Clean the potentially different structure of published HTML
        return cleanGoogleDocsExportedHtml(html); 
      },

      // Approach 3: Try export as text format (Fallback)
      async () => {
        const exportUrl = `https://docs.google.com/document/d/${docId}/export?format=txt`;
        console.log('Trying text export URL (fallback):', exportUrl);

        const response = await fetch(exportUrl, {
          method: 'GET',
          mode: 'cors',
          headers: {
            'Accept': 'text/plain'
          }
        });

        if (!response.ok) {
          console.log(`Text export failed with status: ${response.status}`);
          return null;
        }

        const text = await response.text();
        if (!text || text.length < 20) {
          console.log('Text export returned empty or very short content');
          return null;
        }

        console.log('Successfully fetched as text (fallback), length:', text.length);
        // Basic conversion: wrap in pre for plain text display if HTML failed
        return `<pre style="white-space: pre-wrap; word-wrap: break-word;">${text}</pre>`;
      },
    ];
    
    // Try each approach in sequence until one succeeds
    for (const approach of approaches) {
      try {
        const content = await approach();
        if (content) {
          return content;
        }
      } catch (err) {
        console.log('Approach failed with error:', err.message);
        // Continue to next approach
      }
    }
    
    // If all approaches fail, return an iframe with Google Docs Viewer
    console.log('All direct fetch approaches failed, returning Google Docs Viewer iframe');
    return generateGoogleDocsViewerIframe(docId);
    
  } catch (error) {
    console.error('Error fetching document content:', error);
    return `Error loading content: ${error.message}. Please try again later.`;
  }
};

/**
 * Format plain text to HTML
 * @param {string} text - Plain text content
 * @returns {string} Formatted HTML
 */
const formatTextToHtml = (text) => {
  if (!text) return 'No content available.';
  
  // Convert line breaks to paragraphs
  const paragraphs = text.split('\n\n');
  return paragraphs
    .filter(p => p.trim() !== '')
    .map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`)
    .join('');
};

/**
 * Generate an iframe HTML for Google Docs Viewer
 * @param {string} docId - Google Doc ID
 * @returns {string} iframe HTML
 */
const generateGoogleDocsViewerIframe = (docId) => {
  const docUrl = `https://docs.google.com/document/d/${docId}`;
  const encodedUrl = encodeURIComponent(docUrl);
  
  return `
    <div class="google-docs-viewer">
      <p class="viewer-notice">Loading document using Google Docs Viewer</p>
      <iframe 
        src="https://docs.google.com/viewer?url=${encodedUrl}&embedded=true" 
        width="100%" 
        height="600px" 
        frameborder="0"
        style="border: 1px solid #ddd; border-radius: 4px;"
      ></iframe>
      <p class="direct-link"><a href="${docUrl}" target="_blank">Open in Google Docs</a></p>
    </div>
  `;
};

/**
 * Clean up the HTML exported from Google Docs
 * @param {string} html - The raw HTML from Google Docs export
 * @returns {string} Cleaned HTML content
 */
const cleanGoogleDocsExportedHtml = (html) => {
  if (!html) return 'No content available.';
  
  try {
    // Create a DOM parser
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Remove Google Docs specific styles and scripts
    const styles = doc.querySelectorAll('style');
    styles.forEach(style => style.remove());
    
    const scripts = doc.querySelectorAll('script');
    scripts.forEach(script => script.remove());
    
    // Remove Google Docs header
    const header = doc.querySelector('head');
    if (header) header.remove();
    
    // Get the body content
    const body = doc.querySelector('body');
    if (!body) return 'No content found in document.';
    
    // Remove Google Docs specific classes
    const elements = body.querySelectorAll('*');
    elements.forEach(el => {
      // Remove classes that start with 'c' followed by a number
      const classes = Array.from(el.classList);
      classes.forEach(className => {
        if (/^c\d+$/.test(className)) {
          el.classList.remove(className);
        }
      });
      
      // Remove style attributes
      el.removeAttribute('style');
    });
    
    // Extract just the content we want (skip Google Docs wrapper elements)
    let content = body.innerHTML;
    
    // Remove any remaining Google Docs specific elements or attributes
    content = content
      .replace(/<div[^>]*class="[^"]*title[^"]*"[^>]*>([^<]*)<\/div>/gi, '<h1>$1</h1>')
      .replace(/<span[^>]*class="[^"]*subtitle[^"]*"[^>]*>([^<]*)<\/span>/gi, '<h2>$1</h2>')
      .replace(/<p[^>]*class="[^"]*subtitle[^"]*"[^>]*>([^<]*)<\/p>/gi, '<h2>$1</h2>')
      .replace(/<img[^>]*style="[^"]*"[^>]*>/gi, match => match.replace(/style="[^"]*"/gi, 'style="max-width:100%;"'))
      .replace(/<hr[^>]*style="[^"]*"[^>]*>/gi, '<hr>')
      .replace(/class="[^"]*c\d+[^"]*"/gi, '')
      .replace(/id="[^"]*"/gi, '')
      .replace(/style="[^"]*"/gi, '');
    
    // Replace all headings with styled divs (for consistent styling in the app)
    content = content
      .replace(/<h1[^>]*>(.*?)<\/h1>/gi, '<div class="doc-heading-1">$1</div>')
      .replace(/<h2[^>]*>(.*?)<\/h2>/gi, '<div class="doc-heading-2">$1</div>')
      .replace(/<h3[^>]*>(.*?)<\/h3>/gi, '<div class="doc-heading-3">$1</div>')
      .replace(/<h4[^>]*>(.*?)<\/h4>/gi, '<div class="doc-heading-4">$1</div>')
      .replace(/<h5[^>]*>(.*?)<\/h5>/gi, '<div class="doc-heading-5">$1</div>')
      .replace(/<h6[^>]*>(.*?)<\/h6>/gi, '<div class="doc-heading-6">$1</div>');
    
    // ============================================================
    // PRINCIPLE-DRIVEN CONTENT CLASSIFICATION
    // ============================================================
    // Core Principle: The COLON is the universal semantic separator
    // "Title: Content" is how humans structure key information
    // We classify based on:
    //   1. Presence/position of colon (structural intent)
    //   2. Content length (substance vs metadata)
    //   3. NO hardcoded text patterns - this must work for ANY document
    //
    // Additional Principle: When CSS provides numbering via counters,
    // strip inline numbers to avoid duplication. Numbers in documents
    // (1., 2., •, etc.) are presentational, not semantic.
    // ============================================================

    // Helper: Strip leading numbers/bullets from text (plain text version)
    // Matches: "1 ", "1. ", "1) ", "• ", "- ", etc.
    // Must normalize &nbsp; entities (left behind by HTML tag stripping) and Unicode
    // non-breaking spaces (\u00A0) so they don't prevent number pattern matching.
    const stripLeadingNumber = (text) => {
      return text.replace(/&nbsp;|\u00A0/gi, ' ').replace(/^[\s]*(\d+[\.\)\s]+|[•\-–—]\s*)/, '').trim();
    };

    // Helper: Strip leading numbers/bullets from HTML content
    // Google Docs wraps content in spans with &nbsp; entities, so we handle:
    // <span>&nbsp;1 </span><span>&nbsp; </span><span>Title</span>
    // Principle: Numbers/bullets at start are presentational, not semantic
    const stripLeadingNumberFromHtml = (html) => {
      let cleaned = html;

      // Normalize &nbsp; to regular space for easier matching
      cleaned = cleaned.replace(/&nbsp;/gi, ' ');

      // Remove leading spans containing only whitespace
      cleaned = cleaned.replace(/^(\s*<span[^>]*>\s*<\/span>\s*)+/gi, '');

      // Remove spans containing just a number (with optional spaces/punctuation)
      // Matches: <span> 1 </span>, <span>1.</span>, <span> 1. </span>, etc.
      cleaned = cleaned.replace(/^(\s*<span[^>]*>\s*\d+[\.\)\s]*\s*<\/span>\s*)+/gi, '');

      // Remove spans containing just whitespace/punctuation after number
      cleaned = cleaned.replace(/^(\s*<span[^>]*>\s*[\.\)\s]*\s*<\/span>\s*)+/gi, '');

      // Handle plain text numbers at the very start (after span removal)
      cleaned = cleaned.replace(/^\s*(\d+[\.\)\s]+|[•\-–—]\s*)/, '');

      return cleaned.trim();
    };

    // Process list items - if they have colon structure, bold the title
    content = content.replace(/<li[^>]*>([\s\S]*?)<\/li>/gi, (match, innerContent) => {
      const textContent = innerContent.replace(/<[^>]*>/g, '').trim();
      const colonIndex = textContent.indexOf(':');

      if (colonIndex > 3 && colonIndex < textContent.length - 10) {
        // Has meaningful title:content structure
        // Strip leading number (handles both HTML spans and plain text)
        const cleanedContent = stripLeadingNumberFromHtml(innerContent);
        const processedContent = cleanedContent.replace(/^([^:]+)(:)/, '<strong>$1</strong>$2');
        return `<li class="key-takeaway-item">${processedContent}</li>`;
      }
      return `<li class="doc-list-item">${innerContent}</li>`;
    });

    // Process top-level divs — Google Docs exports metadata headers as <div> not <p>.
    // Classify them the same way as paragraphs so CSS can hide them.
    content = content.replace(/<div[^>]*>([\s\S]*?)<\/div>/gi, (match, innerContent) => {
      const textContent = innerContent.replace(/<[^>]*>/g, '').trim();
      if (!textContent || textContent.length < 2) return match; // Keep structural divs
      // Metadata patterns: dates, pipe-separated, event names, short labels
      if (textContent.length < 80 && (
        /\|/.test(textContent) || // pipe-separated (Speaker | Date)
        /\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\w*\s+\d{1,2},?\s+\d{4}\b/i.test(textContent) ||
        /^(KEY\s+)?TAKEAWAYS?\s*$/i.test(textContent) ||
        /^(Ramad[h]?an|Muharram|Jashan|Fatimiy+ah)\s+\d{4}/i.test(textContent)
      )) {
        return `<div class="doc-info">${innerContent}</div>`;
      }
      return match; // Leave non-metadata divs unchanged
    });

    // Process paragraphs with principle-driven classification
    content = content.replace(/<p[^>]*>([\s\S]*?)<\/p>/gi, (match, innerContent) => {
      const textContent = innerContent.replace(/<[^>]*>/g, '').trim();

      // Principle 1: Empty content should be hidden (CSS will handle)
      if (!textContent || textContent.length < 2) {
        return `<p class="doc-empty"></p>`;
      }

      // Find the first meaningful colon — skip digit:digit patterns
      // (verse references like "Quran 5:35", times like "12:30", chapter:verse)
      let colonIndex = -1;
      for (let i = 0; i < textContent.length; i++) {
        if (textContent[i] === ':') {
          if (i > 0 && /\d/.test(textContent[i - 1]) && i < textContent.length - 1 && /\d/.test(textContent[i + 1])) {
            continue; // skip digit:digit (verse refs, times)
          }
          colonIndex = i;
          break;
        }
      }

      if (colonIndex > 0) {
        const beforeColon = textContent.substring(0, colonIndex).trim();
        const afterColon = textContent.substring(colonIndex + 1).trim();

        // Strip leading number from the title portion for comparison
        const cleanTitle = stripLeadingNumber(beforeColon);

        // Principle 2: Title:Content structure with substantial content = TAKEAWAY
        // A proper takeaway has: meaningful title (>3 chars, <50 chars) + substantial explanation (>30 chars)
        // The <50 char cap prevents long sentences with incidental colons from being treated as titles
        if (cleanTitle.length > 3 && cleanTitle.length < 50 && afterColon.length > 30) {
          // This is a complete takeaway (title + content in one paragraph)
          // Strip leading number (handles both HTML spans and plain text)
          const cleanedContent = stripLeadingNumberFromHtml(innerContent);
          const processedContent = cleanedContent.replace(/^([^:]+)(:)/, '<strong>$1</strong>$2');
          return `<p class="key-takeaway-item">${processedContent}</p>`;
        }

        // Principle 3: Title: with little/no content after = standalone title
        // The explanation likely follows in the next paragraph
        if (cleanTitle.length > 3 && cleanTitle.length < 50 && afterColon.length < 20) {
          const cleanedContent = stripLeadingNumberFromHtml(innerContent);
          const processedContent = cleanedContent.replace(/^([^:]+)(:)/, '<strong>$1</strong>$2');
          return `<p class="takeaway-title">${processedContent}</p>`;
        }
      }

      // Principle 4: Long text without colon structure = explanatory content
      // This is likely the body text following a title, or standalone insight
      if (textContent.length > 80) {
        return `<p class="takeaway-content">${innerContent}</p>`;
      }

      // Principle 5: Short text without structure = metadata/info
      // Could be: title, date, author, section header, etc.
      // CSS will handle presentation based on position
      return `<p class="doc-info">${innerContent}</p>`;
    });

    // Add a content wrapper
    content = `<div class="google-docs-content">${content}</div>`;
    
    return content;
  } catch (error) {
    console.error('Error cleaning Google Docs HTML:', error);
    return 'Error processing content. Please try again later.';
  }
};

/**
 * Fetch a specific lecture by ID
 * @param {string} sheetId - The ID of the Google Sheet.
 * @param {string} lectureId - The ID of the lecture to fetch
 * @returns {Promise<Object>} Lecture object
 */
export const fetchLectureById = async (sheetId, lectureId) => {
  if (!sheetId) {
    console.error('fetchLectureById called without a sheetId!');
    return null;
  }
  try {
    // Fetch all lectures first using the provided sheetId
    const lectures = await fetchLectureData(sheetId);
    
    // Find the specific lecture by its ID
    const lecture = lectures.find(l => l.lectureId === lectureId);
    
    if (lecture) {
      console.log('Found lecture by ID:', lecture);
    } else {
      console.error('Lecture not found with ID:', lectureId);
      console.log('Available lecture IDs:', lectures.map(l => l.lectureId));
    }
    
    return lecture;
  } catch (error) {
    console.error('Error fetching lecture by ID:', error);
    throw error;
  }
};

/**
 * Fetch lectures filtered by speaker
 * @param {string} sheetId - The ID of the Google Sheet.
 * @param {string} speaker - Speaker name to filter by
 * @returns {Promise<Array>} Array of filtered lecture objects
 */
export const fetchLecturesBySpeaker = async (sheetId, speaker) => {
  if (!sheetId) {
    console.error('fetchLecturesBySpeaker called without a sheetId!');
    return [];
  }
  try {
    const lectures = await fetchLectureData(sheetId);
    
    // Filter lectures by speaker name (case-insensitive)
    const filteredLectures = lectures.filter(lecture => 
      lecture.speaker && lecture.speaker.toLowerCase().includes(speaker.toLowerCase())
    );
    
    return filteredLectures;
  } catch (error) {
    console.error('Error fetching lectures by speaker:', error);
    throw error;
  }
};

/**
 * Fetch lectures filtered by topic
 * @param {string} sheetId - The ID of the Google Sheet.
 * @param {string} topic - Topic to filter by
 * @returns {Promise<Array>} Array of filtered lecture objects
 */
export const fetchLecturesByTopic = async (sheetId, topic) => {
  if (!sheetId) {
    console.error('fetchLecturesByTopic called without a sheetId!');
    return [];
  }
  try {
    const lectures = await fetchLectureData(sheetId);
    
    // Filter lectures by topic (case-insensitive)
    const filteredLectures = lectures.filter(lecture => 
      lecture.tags && lecture.tags.some(tag => tag.toLowerCase().includes(topic.toLowerCase()))
    );
    
    return filteredLectures;
  } catch (error) {
    console.error('Error fetching lectures by topic:', error);
    throw error;
  }
};

/**
 * Search lectures by query
 * @param {string} sheetId - The ID of the Google Sheet.
 * @param {string} query - Search query
 * @returns {Promise<Array>} Array of matching lecture objects
 */
export const searchLectures = async (sheetId, query) => {
  if (!sheetId) {
    console.error('searchLectures called without a sheetId!');
    return [];
  }
  if (!query) {
    return []; // Return empty if query is empty
  }
  
  try {
    const lectures = await fetchLectureData(sheetId);
    const lowerCaseQuery = query.toLowerCase();
    
    // Filter lectures based on query matching title, speaker, or topic
    const filteredLectures = lectures.filter(lecture => {
      return (
        // Search in title
        (lecture.title && lecture.title.toLowerCase().includes(lowerCaseQuery)) ||
        // Search in speaker
        (lecture.speaker && lecture.speaker.toLowerCase().includes(lowerCaseQuery)) ||
        // Search in event/series
        (lecture.event && lecture.event.toLowerCase().includes(lowerCaseQuery)) ||
        // Search in tags
        (lecture.tags && lecture.tags.some(tag => tag.toLowerCase().includes(lowerCaseQuery)))
      );
    });
    
    return filteredLectures;
  } catch (error) {
    console.error('Error searching lectures:', error);
    throw error;
  }
};

/**
 * Get events grouped by year from Google Sheets data
 * @param {string} sheetId - The ID of the Google Sheet
 * @returns {Promise<Object>} Object with years as keys and arrays of EventMeta as values
 */
export const getEventsGroupedByYear = async (sheetId) => {
  if (!sheetId) {
    console.error('getEventsGroupedByYear called without a sheetId!');
    return {};
  }

  try {
    console.log('Fetching events grouped by year from sheet:', sheetId);
    
    // Fetch all lectures from Google Sheets
    const lectures = await fetchLectureData(sheetId);
    
    if (!lectures || lectures.length === 0) {
      console.log('No lectures found in sheet');
      return {};
    }
    
    // Group lectures by event/series
    const lecturesByEvent = {};
    
    lectures.forEach(lecture => {
      const eventName = lecture.series || lecture.event || 'Uncategorized';
      
      if (!lecturesByEvent[eventName]) {
        lecturesByEvent[eventName] = [];
      }
      
      lecturesByEvent[eventName].push(lecture);
    });
    
    console.log('Found events:', Object.keys(lecturesByEvent));
    
    // Convert grouped lectures to EventMeta objects
    const eventsByYear = {};
    
    Object.entries(lecturesByEvent).forEach(([eventName, eventLectures]) => {
      if (eventLectures.length === 0) return;
      
      // Extract year and month from the first lecture's date
      const firstLecture = eventLectures[0];
      let year = new Date().getFullYear(); // Default to current year
      let month = null; // Will store month name
      
      if (firstLecture.date) {
        try {
          // Handle different date formats (MM/DD/YYYY, YYYY-MM-DD, etc.)
          const dateStr = firstLecture.date.toString();
          let parsedDate;
          
          if (dateStr.includes('/')) {
            // Handle MM/DD/YYYY format
            const [month, day, yearStr] = dateStr.split('/');
            parsedDate = new Date(parseInt(yearStr), parseInt(month) - 1, parseInt(day));
          } else if (dateStr.includes('-')) {
            // Handle YYYY-MM-DD or DD-MM-YYYY format
            parsedDate = new Date(dateStr);
          } else {
            // Try direct parsing
            parsedDate = new Date(dateStr);
          }
          
          if (!isNaN(parsedDate.getTime())) {
            year = parsedDate.getFullYear();
            // Get month name
            const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                              'July', 'August', 'September', 'October', 'November', 'December'];
            month = monthNames[parsedDate.getMonth()];
          }
        } catch (error) {
          console.warn('Could not parse date for event:', eventName, 'date:', firstLecture.date);
        }
      }
      
      // Get thumbnail URL (first lecture's summary video thumbnail or fallback)
      let thumbnailUrl = '';
      if (firstLecture.summaryVideoUrl) {
        // Extract YouTube thumbnail if it's a YouTube URL
        const youtubeMatch = firstLecture.summaryVideoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]+)/);
        if (youtubeMatch) {
          thumbnailUrl = `https://img.youtube.com/vi/${youtubeMatch[1]}/maxresdefault.jpg`;
        }
      }
      
      // Get primary speaker using the utility function
      const primarySpeaker = getPrimarySpeaker(eventLectures);
      
      // Create EventMeta object
      const eventMeta = {
        id: eventName,
        name: eventName,
        year: year,
        month: month,
        thumbnailUrl: thumbnailUrl,
        primarySpeaker: primarySpeaker,
        lectureCount: eventLectures.length
      };
      
      // Group by year
      if (!eventsByYear[year]) {
        eventsByYear[year] = [];
      }
      
      eventsByYear[year].push(eventMeta);
    });
    
    // Sort events within each year by name
    Object.keys(eventsByYear).forEach(year => {
      eventsByYear[year].sort((a, b) => a.name.localeCompare(b.name));
    });
    
    console.log('Events grouped by year:', eventsByYear);
    return eventsByYear;
    
  } catch (error) {
    console.error('Error getting events grouped by year:', error);
    return {};
  }
};
